package com.videoanalyzer.web.components.auth

import androidx.compose.runtime.*
import org.jetbrains.compose.web.attributes.*
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*

/**
 * Authentication pages: SignIn, SignUp, ForgotPassword, ResetPassword.
 */

@Composable
fun SignInPage(
    onSignInWithGoogle: () -> Unit,
    onSignInWithEmail: (email: String, password: String) -> Unit,
    onGoToSignUp: () -> Unit,
    onGoToForgotPassword: () -> Unit,
    onBack: () -> Unit,
    onTermsOfService: () -> Unit,
    onPrivacyPolicy: () -> Unit,
    isLoading: Boolean,
    errorMessage: String?,
    successMessage: String?
) {
    var email by remember { mutableStateOf("") }
    var password by remember { mutableStateOf("") }
    var localError by remember { mutableStateOf<String?>(null) }

    AuthPageLayout(
        title = "Welcome Back",
        subtitle = "Sign in to continue analyzing your videos",
        onBack = onBack
    ) {
        // Error/Success messages
        DisplayMessages(errorMessage ?: localError, successMessage)

        // Social login
        SocialLoginButtons(onSignInWithGoogle, isLoading)

        // Divider
        AuthDivider()

        // Email form
        EmailPasswordForm(
            email = email,
            onEmailChange = { email = it },
            password = password,
            onPasswordChange = { password = it },
            isLoading = isLoading,
            buttonText = "Sign In",
            onSubmit = {
                localError = null
                if (email.isBlank() || password.isBlank()) {
                    localError = "Please fill in all fields"
                } else {
                    onSignInWithEmail(email, password)
                }
            },
            onForgotPassword = onGoToForgotPassword
        )

        // Switch to sign up
        AuthSwitch(
            text = "Don't have an account?",
            linkText = "Sign up",
            onClick = onGoToSignUp
        )

        // Terms
        TermsLinks(onTermsOfService, onPrivacyPolicy)
    }
}

@Composable
fun SignUpPage(
    onSignInWithGoogle: () -> Unit,
    onSignUpWithEmail: (email: String, password: String) -> Unit,
    onGoToSignIn: () -> Unit,
    onBack: () -> Unit,
    onTermsOfService: () -> Unit,
    onPrivacyPolicy: () -> Unit,
    isLoading: Boolean,
    errorMessage: String?,
    successMessage: String?
) {
    var email by remember { mutableStateOf("") }
    var password by remember { mutableStateOf("") }
    var confirmPassword by remember { mutableStateOf("") }
    var agreedToTerms by remember { mutableStateOf(false) }
    var localError by remember { mutableStateOf<String?>(null) }

    AuthPageLayout(
        title = "Create Account",
        subtitle = "Start analyzing your videos for free",
        onBack = onBack
    ) {
        // Error/Success messages
        DisplayMessages(errorMessage ?: localError, successMessage)

        // Social login
        SocialLoginButtons(onSignInWithGoogle, isLoading)

        // Divider
        AuthDivider()

        // Email form with confirm password
        Div({
            style {
                display(DisplayStyle.Flex)
                flexDirection(FlexDirection.Column)
                gap(16.px)
            }
        }) {
            FormField("Email") {
                Input(InputType.Email) {
                    classes("input")
                    value(email)
                    onInput { email = it.value }
                    attr("placeholder", "you@example.com")
                    style {
                        width(100.percent)
                        padding(14.px, 16.px)
                        fontSize(15.px)
                    }
                    if (isLoading) disabled()
                }
            }

            FormField("Password") {
                Input(InputType.Password) {
                    classes("input")
                    value(password)
                    onInput { password = it.value }
                    attr("placeholder", "At least 6 characters")
                    style {
                        width(100.percent)
                        padding(14.px, 16.px)
                        fontSize(15.px)
                    }
                    if (isLoading) disabled()
                }
            }

            FormField("Confirm Password") {
                Input(InputType.Password) {
                    classes("input")
                    value(confirmPassword)
                    onInput { confirmPassword = it.value }
                    attr("placeholder", "Confirm your password")
                    style {
                        width(100.percent)
                        padding(14.px, 16.px)
                        fontSize(15.px)
                    }
                    if (isLoading) disabled()
                }
            }

            // Terms agreement checkbox
            Div({
                style {
                    display(DisplayStyle.Flex)
                    alignItems(AlignItems.FlexStart)
                    gap(10.px)
                    marginTop(8.px)
                }
            }) {
                Input(InputType.Checkbox) {
                    id("terms-checkbox")
                    checked(agreedToTerms)
                    onChange { agreedToTerms = it.value }
                    style {
                        width(18.px)
                        height(18.px)
                        marginTop(2.px)
                        cursor("pointer")
                        property("accent-color", "#E53935")
                    }
                    if (isLoading) disabled()
                }
                Label(forId = "terms-checkbox") {
                    Span({
                        style {
                            fontSize(13.px)
                            color(Color("#94a3b8"))
                            lineHeight("1.5")
                            cursor("pointer")
                        }
                    }) {
                        Text("I agree to the ")
                        Span({
                            style {
                                color(Color("#0EA5E9"))
                                cursor("pointer")
                                property("text-decoration", "underline")
                            }
                            onClick { onTermsOfService() }
                        }) { Text("Terms of Service") }
                        Text(" and ")
                        Span({
                            style {
                                color(Color("#0EA5E9"))
                                cursor("pointer")
                                property("text-decoration", "underline")
                            }
                            onClick { onPrivacyPolicy() }
                        }) { Text("Privacy Policy") }
                    }
                }
            }

            Button({
                classes("btn", "btn-primary", "btn-large")
                style {
                    width(100.percent)
                    marginTop(8.px)
                    backgroundColor(Color("#E53935"))
                    property("box-shadow", "0 4px 20px rgba(255, 0, 0, 0.35)")
                }
                onClick {
                    localError = null
                    when {
                        email.isBlank() || password.isBlank() || confirmPassword.isBlank() -> {
                            localError = "Please fill in all fields"
                        }
                        password.length < 6 -> {
                            localError = "Password must be at least 6 characters"
                        }
                        password != confirmPassword -> {
                            localError = "Passwords do not match"
                        }
                        !agreedToTerms -> {
                            localError = "Please agree to the Terms of Service and Privacy Policy"
                        }
                        else -> {
                            onSignUpWithEmail(email, password)
                        }
                    }
                }
                if (isLoading) disabled()
            }) {
                if (isLoading) {
                    LoadingButtonContent()
                } else {
                    Text("Create Account")
                }
            }
        }

        // Switch to sign in
        AuthSwitch(
            text = "Already have an account?",
            linkText = "Sign in",
            onClick = onGoToSignIn
        )
    }
}

@Composable
fun ForgotPasswordPage(
    onSubmit: (email: String) -> Unit,
    onBack: () -> Unit,
    isLoading: Boolean,
    errorMessage: String?,
    successMessage: String?
) {
    var email by remember { mutableStateOf("") }
    var localError by remember { mutableStateOf<String?>(null) }

    AuthPageLayout(
        title = "Reset Password",
        subtitle = "Enter your email and we'll send you a reset link",
        onBack = onBack
    ) {
        // Error/Success messages
        DisplayMessages(errorMessage ?: localError, successMessage)

        Div({
            style {
                display(DisplayStyle.Flex)
                flexDirection(FlexDirection.Column)
                gap(16.px)
            }
        }) {
            FormField("Email") {
                Input(InputType.Email) {
                    classes("input")
                    value(email)
                    onInput { email = it.value }
                    attr("placeholder", "you@example.com")
                    style {
                        width(100.percent)
                        padding(14.px, 16.px)
                        fontSize(15.px)
                    }
                    if (isLoading) disabled()
                }
            }

            Button({
                classes("btn", "btn-primary", "btn-large")
                style {
                    width(100.percent)
                    marginTop(8.px)
                    backgroundColor(Color("#E53935"))
                    property("box-shadow", "0 4px 20px rgba(255, 0, 0, 0.35)")
                }
                onClick {
                    localError = null
                    if (email.isBlank()) {
                        localError = "Please enter your email address"
                    } else if (!email.contains("@")) {
                        localError = "Please enter a valid email address"
                    } else {
                        onSubmit(email)
                    }
                }
                if (isLoading) disabled()
            }) {
                if (isLoading) {
                    LoadingButtonContent()
                } else {
                    Text("Send Reset Link")
                }
            }
        }

        // Back to sign in
        AuthSwitch(
            text = "Remember your password?",
            linkText = "Sign in",
            onClick = onBack
        )
    }
}

@Composable
fun ResetPasswordPage(
    onSubmit: (newPassword: String) -> Unit,
    onBack: () -> Unit,
    isLoading: Boolean,
    errorMessage: String?,
    successMessage: String?
) {
    var password by remember { mutableStateOf("") }
    var confirmPassword by remember { mutableStateOf("") }
    var localError by remember { mutableStateOf<String?>(null) }

    AuthPageLayout(
        title = "Set New Password",
        subtitle = "Enter your new password below",
        onBack = onBack
    ) {
        // Error/Success messages
        DisplayMessages(errorMessage ?: localError, successMessage)

        Div({
            style {
                display(DisplayStyle.Flex)
                flexDirection(FlexDirection.Column)
                gap(16.px)
            }
        }) {
            FormField("New Password") {
                Input(InputType.Password) {
                    classes("input")
                    value(password)
                    onInput { password = it.value }
                    attr("placeholder", "At least 6 characters")
                    style {
                        width(100.percent)
                        padding(14.px, 16.px)
                        fontSize(15.px)
                    }
                    if (isLoading) disabled()
                }
            }

            FormField("Confirm New Password") {
                Input(InputType.Password) {
                    classes("input")
                    value(confirmPassword)
                    onInput { confirmPassword = it.value }
                    attr("placeholder", "Confirm your new password")
                    style {
                        width(100.percent)
                        padding(14.px, 16.px)
                        fontSize(15.px)
                    }
                    if (isLoading) disabled()
                }
            }

            Button({
                classes("btn", "btn-primary", "btn-large")
                style {
                    width(100.percent)
                    marginTop(8.px)
                    backgroundColor(Color("#E53935"))
                    property("box-shadow", "0 4px 20px rgba(255, 0, 0, 0.35)")
                }
                onClick {
                    localError = null
                    when {
                        password.isBlank() || confirmPassword.isBlank() -> {
                            localError = "Please fill in all fields"
                        }
                        password.length < 6 -> {
                            localError = "Password must be at least 6 characters"
                        }
                        password != confirmPassword -> {
                            localError = "Passwords do not match"
                        }
                        else -> {
                            onSubmit(password)
                        }
                    }
                }
                if (isLoading) disabled()
            }) {
                if (isLoading) {
                    LoadingButtonContent()
                } else {
                    Text("Update Password")
                }
            }
        }

        // Back to sign in
        AuthSwitch(
            text = "Changed your mind?",
            linkText = "Back to sign in",
            onClick = onBack
        )
    }
}
